import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Edit, Briefcase, Check, Building } from "lucide-react";
import { createUserExperience, getUserExperience, UpdateUserExperience, useAppDispatch } from "../../../../redux";
import {  GlobalService } from "../../../shared";
import ExperienceModal from "../../modals/ExperienceModal";
import Button from "../../../../components/ui/Button";
import Card from "../../../../components/ui/card";
import { useTranslation } from "react-i18next";

export default function Experience({ userId, showEditIcon = true }: { userId?: string; showEditIcon?: boolean }) {
    const { t } = useTranslation();
    const [showExperienceModal, setShowExperienceModal] = useState(false);
    const [editExperienceData, setEditExperienceData] = useState(null);
    const [experienceData, setExperienceData] = useState([]);
    const [originalexperienceData, setOriginalexperienceData] = useState([]);

    const dispatch = useAppDispatch();

    const { experience } = useSelector((state: any) => state.professional);
    const { user } = useSelector((state: any) => state.auth);
    const targetUserId = userId || user?.id; // Use passed userId or fallback to current user

    const handleExperienceSave = async (data: any[]) => {
        const newItems = data.filter(item => !item.id);
        const existingItems = data.filter(item => item.id);
        let updatedCount = 0;

        // Only update changed items
        for (const item of existingItems) {
            const original = originalexperienceData.find(e => e.id === item.id);

            const hasChanged =
                item.title !== original?.title ||
                item.company !== original?.company ||
                item.period !== original?.period ||
                item.description !== original?.description ||
                JSON.stringify(item.achievements || []) !== JSON.stringify(original?.achievements || []);

            if (!hasChanged) continue;

            const updatePayload = {
                userId: { id: targetUserId },
                achievements: item.achievements || [],
                description: item.description,
                company: item.company,
                period: item.period,
                title: item.title,
            };

            await dispatch(UpdateUserExperience({ id: item.id, data: updatePayload }));
            updatedCount++;
        }

        // Create new items
        let createdItems = [];
        if (newItems.length > 0) {
            const createPayload = newItems.map(item => ({
                userId: { id: targetUserId },
                achievements: item.achievements || [],
                description: item.description,
                period: item.period,
                company: item.company,
                title: item.title,
            }));

            const result = await dispatch(createUserExperience({ data: createPayload }));

            if (createUserExperience.fulfilled.match(result)) {
                createdItems = result.payload;
            }
        }

        // Update local and original state
        const updatedData = [...existingItems, ...createdItems];

        setExperienceData(updatedData);
        setOriginalexperienceData(updatedData); // sync original for accurate future diffs
        if (updatedCount > 0 && newItems.length === 0) {
            GlobalService.showToast("success", t("experience.successTitle"), t("experience.updateSuccess"));
        }
    };

    useEffect(() => {
        getAllExperiences();
    }, [targetUserId]);

    const getAllExperiences = async () => {
        await dispatch(getUserExperience({ id: targetUserId }));
    };
    useEffect(() => {
        if (!experience) return;

        setExperienceData(experience);
        setOriginalexperienceData(experience);
    }, [experience]);

    return (
        <>
            <Card color={undefined} className="p-6">
                <div className="flex justify-between items-start mb-4">
                    <h2 className="text-xl font-semibold">{t("experience.title")}</h2>
                    {showEditIcon && (
                        <Button
                            variant="ghost"
                            size="sm"
                             className="text-blue-600 hover:text-blue-700"
                            onClick={() => {
                                setEditExperienceData(experienceData);
                                setShowExperienceModal(true);
                            }}>
                            <Edit className="w-4 h-4" />
                        </Button>
                    )}
                </div>
                <div className="space-y-6">
                    {experienceData.map((exp, index) => (
                        <div key={index} className="border-l-2 border-gray-200 pl-4 py-2">
                            <div>
                                <h3 className="font-medium text-gray-900">{exp.title}</h3>
                            </div>
                            <p className="text-sm text-gray-600">{exp.company}</p>
                            <p className="text-sm text-gray-500">{exp.period}</p>
                            <p className="mt-2 text-gray-600">{exp.description}</p>
                            <ul className="mt-2 space-y-1">
                                {exp.achievements.map((achievement, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                                        <Check className="w-4 h-4 text-green-500 mt-1" />
                                        <span>{achievement}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </Card>

            <ExperienceModal
                open={showExperienceModal}
                onClose={() => setShowExperienceModal(false)}
                onSave={handleExperienceSave}
                initialData={editExperienceData}
            />
        </>
    );
}
